/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';

import gridBackground from '../../../../assets/grid-background.svg';
import { SectionName } from '../../../../common/analytics/section-name';
import { useSectionReveal } from '../../../../common/analytics/useSectionReveal';

import { Pillars } from './Pillars';
import { Stats } from './Stats';

// OverviewSection renders the merged "Stats + Values" block from the Figma
// design. Both rows live inside a SINGLE 1200px wrapper so the outer dotted
// borders (left + right of `content`, and top of the stats row + bottom of
// the pillars row) are owned by ONE element each, guaranteeing the dot
// pattern is one continuous stroke across element boundaries with no
// doubling and no gaps.
//
//   ┌──────────┬──────────┬──────────┬──────────┐  ── stats row top
//   │  stat 1  │  stat 2  │  stat 3  │  stat 4  │  240px tall   (Stats)
//   ├──────────┴────┬─────┴──────────┴──────────┤
//   │               │       value-pillar 1      │
//   │   graphic     ├───────────────────────┤  720px tall   (Pillars)
//   │               │       value-pillar 2      │
//   │               ├───────────────────────┤
//   │               │       value-pillar 3      │
//   └───────────────┴───────────────────────┘  ── pillars row bottom
//
// The two rows live in sibling files so the data-driven `Stats` (which
// suspends on `GlobalMetricsAtom`) can be wrapped in its own boundary
// without affecting the static `Pillars`.

const styles = cssMap({
	section: {
		width: '100%',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingTop: '120px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingBottom: '120px',
		backgroundSize: '60px 60px',
		backgroundRepeat: 'repeat',
	},
	content: {
		width: '100%',
		maxWidth: '1200px',
		marginInline: 'auto',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#111213',
	},
});

export function OverviewSection(): JSX.Element {
	const sectionRef = useSectionReveal<HTMLElement>(SectionName.Overview);
	return (
		<section
			ref={sectionRef}
			css={styles.section}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
			style={{ backgroundImage: `url(${gridBackground})` }}
		>
			<div css={styles.content}>
				<Stats />
				<Pillars />
			</div>
		</section>
	);
}
